/**
 * <TutorStudentObjectivesCard> — « Ses cours avec toi », dans l'Aperçu de la fiche (organism).
 *
 * Le bloc « Objectifs » de `StudentProfil.js`, dans son ordre :
 *  1. la formule, sa durée et l'offre en cours, sur une ligne ;
 *  2. ses objectifs pédagogiques ouverts (`ObjectivesSummaryDisplay`, en lecture pour un tuteur) —
 *     leur matière, le libellé, les heures de la semaine, ses lives (« Mardi 17:00 ») et leur état
 *     (« En cours », « Demandé ») ; sans objectif, le bloc s'appelle « Matières » et montre ses
 *     matières prioritaires (`PrioritizedSchoolSubjects`) ;
 *  3. « Précisions sur le besoin », en deux lignes puis « Lire la suite » ;
 *  4. « Programme de révision », quand l'élève en a un — sa lecture, en lecture seule.
 *
 * Les gestes d'admin du legacy (valider, clôturer, créer un objectif) ne sont pas ici : un tuteur
 * ne les a pas, et l'admin les fait dans le BO.
 */
import React, { useState } from "react"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Divider } from "../../atoms/Divider"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { SettingsSection } from "../../molecules/SettingsSection"
import { SubjectBadges } from "../../molecules/SubjectBadges"
import {
    type UITutorObjectiveStatus,
    type UITutorStudentObjective,
    type UITutorStudentProfile,
} from "../../../ports/primary/ui/types/tutorEleves"

const STATUS: Record<UITutorObjectiveStatus, { text: string; variant: "success" | "warning" }> = {
    ongoing: { text: "En cours", variant: "success" },
    requested: { text: "Demandé", variant: "warning" },
}

export type TutorStudentObjectivesCardProps = {
    profile: UITutorStudentProfile
    /** « Programme de révision » — sa lecture. Monté seulement si l'élève en a un. */
    onOpenStudyProgram?: () => void
    testID?: string
}

function ObjectiveLine(props: { objective: UITutorStudentObjective }) {
    const { objective } = props
    const status = objective.status != null ? STATUS[objective.status] : null
    return (
        <Row gap="3" align="start">
            <SubjectBadges subjects={objective.subjects} showLabel={false} />
            <Stack gap="1" style={styles.body}>
                <Text size="text-md" weight="medium">
                    {objective.title}
                </Text>
                <Row gap="2" align="center" style={styles.wrap}>
                    {objective.hoursLabel != null && (
                        <Text size="text-sm" weight="semibold" color="role-strong">
                            {objective.hoursLabel}
                        </Text>
                    )}
                    {objective.schedule.map((slot) => (
                        <Badge key={slot} text={slot} variant="neutral" iconName="calendar-alt" />
                    ))}
                    {status != null && <Badge text={status.text} variant={status.variant} tinted />}
                </Row>
            </Stack>
        </Row>
    )
}

export function TutorStudentObjectivesCard(props: TutorStudentObjectivesCardProps) {
    const { profile, onOpenStudyProgram, testID } = props
    const [precisionsOpen, setPrecisionsOpen] = useState(false)
    const plan = [profile.offerLabel, profile.planLabel, profile.planDurationLabel]
        .filter((part) => part != null && part !== "")
        .join(" · ")
    const hasObjectives = profile.objectives.length > 0
    const subjects = profile.student.subjects

    return (
        <SettingsSection title={hasObjectives ? "Ses cours avec toi" : "Matières"} testID={testID}>
            <Stack gap="3">
                {plan !== "" && (
                    <Text size="text-sm" color="text-muted">
                        {plan}
                    </Text>
                )}
                {hasObjectives ? (
                    <Stack gap="0">
                        {profile.objectives.map((objective, index) => (
                            <React.Fragment key={objective.id}>
                                {index > 0 && <Divider spacing="3" />}
                                <ObjectiveLine objective={objective} />
                            </React.Fragment>
                        ))}
                    </Stack>
                ) : subjects.length > 0 ? (
                    <Row gap="2" style={styles.wrap}>
                        {subjects.map((subject) => (
                            <SubjectBadge key={subject.id} subject={subject} showLabel fullLabel />
                        ))}
                    </Row>
                ) : (
                    <Text size="text-md" color="text-muted">
                        -
                    </Text>
                )}

                {profile.requestPrecisions != null && profile.requestPrecisions !== "" && (
                    <Card elevation="none" background="surface-subtle" p="3">
                        <Stack gap="1">
                            <Text size="text-sm" weight="bold" color="text-muted">
                                Précisions sur le besoin
                            </Text>
                            <Text size="text-sm" numberOfLines={precisionsOpen ? undefined : 2}>
                                {profile.requestPrecisions}
                            </Text>
                            {!precisionsOpen && (
                                <Row>
                                    <TextButton
                                        label="Lire la suite"
                                        size="sm"
                                        onPress={() => setPrecisionsOpen(true)}
                                    />
                                </Row>
                            )}
                        </Stack>
                    </Card>
                )}

                {profile.hasStudyProgram === true && onOpenStudyProgram != null && (
                    <Row>
                        <TextButton label="Programme de révision" size="sm" onPress={onOpenStudyProgram} />
                    </Row>
                )}
            </Stack>
        </SettingsSection>
    )
}

const styles = StyleSheet.create(() => ({
    body: { flex: 1, minWidth: 0 },
    wrap: { flexWrap: "wrap" },
}))
